import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
    fetchMFAPreference,
    setUpTOTP,
    signOut,
    updateMFAPreference,
    updatePassword,
    verifyTOTPSetup,
} from 'aws-amplify/auth';
import QRCode from 'qrcode';
import { useNavigate } from 'react-router-dom';

/**
 * Display name shown inside the otpauth URI Cognito hands back. Authenticator
 * apps (1Password, Authy, Google Authenticator) render this as the issuer
 * label next to the code, so it should match the product name users see.
 */
const TOTP_ISSUER = 'Accedo';

/** Minimum password length required by the Cognito password policy. */
const MIN_PASSWORD_LENGTH = 8;

/** Auto-dismiss delay for transient success Alerts (matches ProfilePanel / BillingPanel rhythm). */
const SUCCESS_AUTODISMISS_MS = 3000;

/**
 * 2FA section state machine.
 *
 *   loading            → still resolving `fetchMFAPreference` on mount
 *   not-enrolled       → no TOTP enrolled; "Enable 2FA" CTA
 *   enrolling-show-qr  → setUpTOTP done, showing the QR + secret
 *   enrolling-verify   → user typed a code; verifyTOTPSetup in flight
 *   enrolled           → TOTP is the user's preferred MFA
 *   disabling          → updateMFAPreference({ totp: 'DISABLED' }) in flight
 */
type TwoFactorPhase =
    | 'loading'
    | 'not-enrolled'
    | 'enrolling-show-qr'
    | 'enrolling-verify'
    | 'enrolled'
    | 'disabling';

/**
 * Settings → Security panel (issue #1949).
 *
 * Three sections:
 *
 * 1. **Sign out everywhere** — a single CTA that calls Cognito's
 *    GlobalSignOut via Amplify, then sends the user back to /login. Cognito
 *    does not expose an API to enumerate active sessions or revoke a
 *    specific one; until we maintain our own session table (#1967), this
 *    section is the only honest thing we can ship here.
 * 2. **Two-factor authentication** — TOTP enrollment / disable flow. The
 *    per-tenant Cognito pool ships with `Mfa.OPTIONAL` so non-enrolled
 *    users still sign in non-interactively; users who enroll here get a
 *    real TOTP challenge on their next login.
 * 3. **Audit log** — link into the per-tenant Activity Log (added in a
 *    follow-up commit on this branch).
 */
export default function SecurityPanel() {
    const navigate = useNavigate();

    // ---- Sign out everywhere state ----
    const [signingOut, setSigningOut] = useState(false);
    const [signOutError, setSignOutError] = useState<string | null>(null);

    // ---- Change password state (#2169 — moved from ProfilePanel) ----
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [passwordSuccess, setPasswordSuccess] = useState(false);

    // ---- 2FA section state ----
    const [twoFactorPhase, setTwoFactorPhase] = useState<TwoFactorPhase>('loading');
    const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
    const [totpSecret, setTotpSecret] = useState<string | null>(null);
    const [verificationCode, setVerificationCode] = useState('');
    const [twoFactorError, setTwoFactorError] = useState<string | null>(null);

    // Resolve initial 2FA preference once on mount. `fetchMFAPreference`
    // returns `{ preferred?, enabled? }`; only `preferred === 'TOTP'` means
    // the user has enrolled TOTP and Cognito will challenge them. Anything
    // else (undefined, SMS, EMAIL) falls back to the not-enrolled view.
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const pref = await fetchMFAPreference();
                if (cancelled) return;
                setTwoFactorPhase(pref.preferred === 'TOTP' ? 'enrolled' : 'not-enrolled');
            } catch {
                if (!cancelled) {
                    // Fall back to "not enrolled" so the user can attempt
                    // setup. If the network is the issue, setUpTOTP will
                    // surface a clearer error.
                    setTwoFactorPhase('not-enrolled');
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    /**
     * Maps a Cognito error from `updatePassword` to a user-friendly string.
     * `NotAuthorizedException` → wrong current password.
     * `InvalidPasswordException` → Cognito's own message names the missing
     * constraint (digit / symbol / uppercase), so we surface it verbatim.
     */
    function mapPasswordError(error: unknown): string {
        if (error && typeof error === 'object' && 'name' in error) {
            const name = (error as { name?: string }).name;
            if (name === 'NotAuthorizedException') {
                return 'Current password is incorrect.';
            }
            if (name === 'InvalidPasswordException' && 'message' in error) {
                return String((error as { message?: string }).message ?? 'Password does not meet the policy.');
            }
            if (name === 'LimitExceededException') {
                return 'Too many attempts. Please wait a few minutes and try again.';
            }
        }
        return 'Failed to change password. Please try again.';
    }

    const handlePasswordSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setPasswordError(null);
        setPasswordSuccess(false);

        if (newPassword.length < MIN_PASSWORD_LENGTH) {
            setPasswordError(`New password must be at least 8 characters long.`);
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError('New passwords do not match.');
            return;
        }

        setPasswordSaving(true);
        try {
            await updatePassword({ oldPassword: currentPassword, newPassword });
            setPasswordSuccess(true);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setTimeout(() => setPasswordSuccess(false), SUCCESS_AUTODISMISS_MS);
        } catch (error) {
            setPasswordError(mapPasswordError(error));
        } finally {
            setPasswordSaving(false);
        }
    };

    const handleSignOutEverywhere = async () => {
        setSignOutError(null);
        setSigningOut(true);
        try {
            // GlobalSignOut revokes every active refresh token for the user
            // — including this one. Sending the user back to /login is what
            // keeps the UI honest after the local tokens are torched.
            await signOut({ global: true });
            navigate('/login');
        } catch {
            setSignOutError('Failed to sign out. Please try again.');
        } finally {
            setSigningOut(false);
        }
    };

    const handleEnable2FA = async () => {
        setTwoFactorError(null);
        try {
            const details = await setUpTOTP();
            // Authenticator apps consume the otpauth:// URI; we render it as a
            // QR code so users don't have to type the shared secret by hand.
            const uri = details.getSetupUri(TOTP_ISSUER).toString();
            const dataUrl = await QRCode.toDataURL(uri);
            setQrDataUrl(dataUrl);
            setTotpSecret(details.sharedSecret);
            setTwoFactorPhase('enrolling-show-qr');
        } catch {
            setTwoFactorError('Failed to start 2FA setup. Please try again.');
        }
    };

    const handleVerifyTOTP = async () => {
        setTwoFactorError(null);
        setTwoFactorPhase('enrolling-verify');
        try {
            await verifyTOTPSetup({ code: verificationCode });
            // Mark TOTP as the preferred factor so Cognito issues the
            // challenge on subsequent sign-ins.
            await updateMFAPreference({ totp: 'PREFERRED' });
            setVerificationCode('');
            setQrDataUrl(null);
            setTotpSecret(null);
            setTwoFactorPhase('enrolled');
        } catch (error) {
            // EnableSoftwareTokenMFAException is the canonical "wrong code"
            // response. Anything else falls through to the generic line so
            // we never expose stack traces in the UI.
            const errName = (error as { name?: string } | undefined)?.name;
            if (errName === 'EnableSoftwareTokenMFAException' || errName === 'CodeMismatchException') {
                setTwoFactorError("That code didn't match. Try again with a fresh code from your authenticator app.");
            } else {
                setTwoFactorError('Failed to verify 2FA. Please try again.');
            }
            // Stay on the verify screen so the user can retype the code.
            setTwoFactorPhase('enrolling-show-qr');
        }
    };

    const handleDisable2FA = async () => {
        setTwoFactorError(null);
        setTwoFactorPhase('disabling');
        try {
            await updateMFAPreference({ totp: 'DISABLED' });
            setTwoFactorPhase('not-enrolled');
        } catch {
            setTwoFactorError('Failed to disable 2FA. Please try again.');
            setTwoFactorPhase('enrolled');
        }
    };

    const handleCancelEnrollment = () => {
        setVerificationCode('');
        setQrDataUrl(null);
        setTotpSecret(null);
        setTwoFactorError(null);
        setTwoFactorPhase('not-enrolled');
    };

    return (
        <Stack spacing={3}>
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" component="h2" gutterBottom>
                    Change password
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Choose a strong password. Your new password must be at least 8 characters and
                    meet your organization's password policy.
                </Typography>

                <Box component="form" onSubmit={handlePasswordSubmit}>
                    <Stack spacing={2}>
                        <TextField
                            label="Current password"
                            type="password"
                            value={currentPassword}
                            onChange={(event) => setCurrentPassword(event.target.value)}
                            disabled={passwordSaving}
                            slotProps={{ htmlInput: { 'aria-label': 'Current password' } }}
                            autoComplete="current-password"
                            fullWidth
                        />
                        <TextField
                            label="New password"
                            type="password"
                            value={newPassword}
                            onChange={(event) => setNewPassword(event.target.value)}
                            disabled={passwordSaving}
                            slotProps={{ htmlInput: { 'aria-label': 'New password' } }}
                            autoComplete="new-password"
                            fullWidth
                        />
                        <TextField
                            label="Confirm new password"
                            type="password"
                            value={confirmPassword}
                            onChange={(event) => setConfirmPassword(event.target.value)}
                            disabled={passwordSaving}
                            slotProps={{ htmlInput: { 'aria-label': 'Confirm new password' } }}
                            autoComplete="new-password"
                            fullWidth
                        />

                        {passwordSuccess && (
                            <Alert severity="success" role="status">
                                Password changed.
                            </Alert>
                        )}
                        {passwordError && (
                            <Alert severity="error" role="alert">
                                {passwordError}
                            </Alert>
                        )}

                        <Box>
                            <Button type="submit" variant="contained" disabled={passwordSaving}>
                                {passwordSaving ? 'Changing...' : 'Change password'}
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </Paper>

            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" component="h2" gutterBottom>
                    Sign out everywhere
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    End every active session for your account — including this one. You'll need
                    to sign in again on every device.
                </Typography>

                {signOutError && (
                    <Alert severity="error" role="alert" sx={{ mb: 2 }}>
                        {signOutError}
                    </Alert>
                )}

                <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleSignOutEverywhere}
                    disabled={signingOut}
                >
                    {signingOut ? 'Signing out...' : 'Sign out everywhere'}
                </Button>
            </Paper>

            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" component="h2" gutterBottom>
                    Two-factor authentication
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Add an extra layer of protection by requiring a one-time code from your
                    authenticator app each time you sign in.
                </Typography>

                {twoFactorPhase === 'loading' && (
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                            Loading 2FA status...
                        </Typography>
                    </Stack>
                )}

                {twoFactorPhase === 'not-enrolled' && (
                    <Stack spacing={2}>
                        {twoFactorError && (
                            <Alert severity="error" role="alert">
                                {twoFactorError}
                            </Alert>
                        )}
                        <Box>
                            <Button variant="contained" onClick={handleEnable2FA}>
                                Enable two-factor authentication
                            </Button>
                        </Box>
                    </Stack>
                )}

                {(twoFactorPhase === 'enrolling-show-qr' || twoFactorPhase === 'enrolling-verify') && (
                    <Stack spacing={2}>
                        <Typography variant="body2">
                            1. Scan this QR code in your authenticator app (or paste the secret
                            manually).
                        </Typography>
                        {qrDataUrl && (
                            <Box>
                                <img src={qrDataUrl} alt="Scan this QR code in your authenticator app" />
                            </Box>
                        )}
                        {totpSecret && (
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                Secret: {totpSecret}
                            </Typography>
                        )}
                        <Typography variant="body2">
                            2. Enter the 6-digit code from your authenticator app to confirm.
                        </Typography>
                        <TextField
                            label="Verification code"
                            value={verificationCode}
                            onChange={(event) => setVerificationCode(event.target.value)}
                            slotProps={{ htmlInput: { 'aria-label': 'Verification code', inputMode: 'numeric' } }}
                            disabled={twoFactorPhase === 'enrolling-verify'}
                            fullWidth
                        />
                        {twoFactorError && (
                            <Alert severity="error" role="alert">
                                {twoFactorError}
                            </Alert>
                        )}
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="contained"
                                onClick={handleVerifyTOTP}
                                disabled={
                                    twoFactorPhase === 'enrolling-verify' || verificationCode.length === 0
                                }
                            >
                                {twoFactorPhase === 'enrolling-verify' ? 'Verifying...' : 'Verify'}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleCancelEnrollment}
                                disabled={twoFactorPhase === 'enrolling-verify'}
                            >
                                Cancel
                            </Button>
                        </Stack>
                    </Stack>
                )}

                {(twoFactorPhase === 'enrolled' || twoFactorPhase === 'disabling') && (
                    <Stack spacing={2}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            <Chip label="2FA enabled" color="success" size="small" />
                            <Typography variant="body2" color="text.secondary">
                                You'll be asked for a one-time code on every new sign-in.
                            </Typography>
                        </Stack>
                        {twoFactorError && (
                            <Alert severity="error" role="alert">
                                {twoFactorError}
                            </Alert>
                        )}
                        <Box>
                            <Button
                                variant="outlined"
                                color="warning"
                                onClick={handleDisable2FA}
                                disabled={twoFactorPhase === 'disabling'}
                            >
                                {twoFactorPhase === 'disabling' ? 'Disabling...' : 'Disable 2FA'}
                            </Button>
                        </Box>
                    </Stack>
                )}
            </Paper>

            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" component="h2" gutterBottom>
                    Audit log
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Every change to policies, users, and settings is recorded in the activity
                    log. Review it to audit any change made by you or your teammates.
                </Typography>
                <Button variant="outlined" onClick={() => navigate('/activity-log')}>
                    View Activity Log
                </Button>
            </Paper>
        </Stack>
    );
}
